import { useState, useRef, useEffect, useCallback } from "react";
import { useAthenaStore } from "./useAthenaStore";
import apiClient from "../../apiClient";
import useUser from "../../customHooks/useUser";

const API_BASE = "/master-view-chat";

// ============================================================
// MasterViewChat (Athena) Component
//
// Usage — just mount once at app root or layout level:
//   <MasterViewChat />
//
// Then trigger from anywhere:
//   const openAthena = useAthenaStore((s) => s.openAthena);
//   openAthena("05755912544");
// ============================================================
export default function MasterViewChat() {
    const { user } = useUser();

    // -- Zustand store --
    const isOpen = useAthenaStore((s) => s.isOpen);
    const mawb = useAthenaStore((s) => s.mawb);
    const minimizeAthena = useAthenaStore((s) => s.minimizeAthena);
    const restoreAthena = useAthenaStore((s) => s.restoreAthena);
    const closeAthena = useAthenaStore((s) => s.closeAthena);
    const setSessionKey = useAthenaStore((s) => s.setSessionKey);

    // -- Local state --
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [sessionInfo, setSessionInfo] = useState(null);
    const [error, setError] = useState(null);
    const [tokenWarning, setTokenWarning] = useState(null);
    const [dataSources, setDataSources] = useState(null);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Focus input when chat opens
    useEffect(() => {
        if (isOpen && isInitialized) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen, isInitialized]);

    // -- API Calls --
    const apiCall = useCallback(async (endpoint, body = {}, method = "post") => {
        const res = await apiClient({
            method,
            url: `${API_BASE}/${endpoint}`,
            data: method !== "get" ? body : undefined,
            headers: {
                'x-user-id': user.s_email
            }
        });
        return res.data;
    }, []);

    // Auto-initialize when mawb changes from the store
    const prevMawbRef = useRef(null);
    useEffect(() => {
        if (mawb && mawb !== prevMawbRef.current) {
            prevMawbRef.current = mawb;
            // Reset local state for new MAWB
            setIsInitialized(false);
            setMessages([]);
            setSessionInfo(null);
            setDataSources(null);
            setTokenWarning(null);
            setError(null);
            // Trigger initialization
            handleInitialize();
        }
    }, [mawb]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInitialize = useCallback(async () => {
        if (!mawb.trim()) return;
        setIsLoading(true);
        setError(null);
        setMessages([]);
        setTokenWarning(null);

        try {
            const data = await apiCall("initialize", { mawb: mawb.trim() });
            setIsInitialized(true);
            setSessionInfo(data);
            setSessionKey(data.sessionKey);
            setDataSources(data.dataSources);
            setMessages([
                {
                    role: "assistant",
                    content: data.summary,
                    timestamp: new Date(),
                    tokens: data.tokensUsed,
                },
            ]);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setIsLoading(false);
        }
    }, [mawb, apiCall]);

    const handleSendMessage = useCallback(async () => {
        const text = inputValue.trim();
        if (!text || isLoading) return;

        const userMsg = { role: "user", content: text, timestamp: new Date() };
        setMessages((prev) => [...prev, userMsg]);
        setInputValue("");
        setIsLoading(true);
        setError(null);

        try {
            const data = await apiCall("message", { mawb, message: text });
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content: data.message,
                    timestamp: new Date(),
                    tokens: data.tokensUsed,
                },
            ]);
            if (data.tokenWarning) setTokenWarning(data.tokenWarning);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, mawb, apiCall]);

    const handleRefresh = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await apiCall("refresh", { mawb });
            setMessages((prev) => [
                ...prev,
                {
                    role: "system",
                    content: "Data refreshed — I'm now using the latest shipment information.",
                    timestamp: new Date(),
                },
            ]);
            if (data.dataSources) setDataSources(data.dataSources);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setIsLoading(false);
        }
    }, [mawb, apiCall]);

    const handleClear = useCallback(async () => {
        setIsLoading(true);
        try {
            await apiCall("clear", { mawb });
            setMessages([
                {
                    role: "system",
                    content: "Conversation cleared. Data context preserved — you can continue asking questions.",
                    timestamp: new Date(),
                },
            ]);
            setTokenWarning(null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setIsLoading(false);
        }
    }, [mawb, apiCall]);

    const handleEndSession = useCallback(async () => {
        try {
            await apiCall("session", { mawb }, "delete");
        } catch (_) { }
        setIsInitialized(false);
        setMessages([]);
        setSessionInfo(null);
        setDataSources(null);
        setTokenWarning(null);
        setError(null);
        prevMawbRef.current = null;
        closeAthena();
    }, [mawb, apiCall, closeAthena]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (d) =>
        new Date(d).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    // Quick suggestion chips
    const suggestions = [
        "Are there any holds on this shipment?",
        "What is the payment status?",
        "Where is the cargo located?",
        "Is customs cleared?",
        "Show the timeline of events",
        "Any piece count discrepancies?",
    ];

    // -- Render --
    // Don't render anything if Athena was never triggered
    if (!mawb && !isOpen) return null;

    return (
        <>
            {/* Floating trigger button — shows when minimized with an active session */}
            {!isOpen && mawb && (
                // @ts-ignore
                <button onClick={restoreAthena} style={styles.fab}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                    <span style={styles.fabLabel}>Athena</span>
                </button>
            )}

            {/* Chat panel */}
            {isOpen && (
                // @ts-ignore
                <div style={styles.panel}>
                    {/* Header */}
                    <div style={styles.header}>
                        <div style={styles.headerLeft}>
                            <div style={styles.headerIcon}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                </svg>
                            </div>
                            <div>
                                <div style={styles.headerTitle}>Athena</div>
                                <div style={styles.headerSub}>
                                    {isInitialized
                                        ? `AWB ${mawb.replace(/^(\d{3})(\d+)$/, "$1-$2")}`
                                        : "Cargo Intelligence Assistant"}
                                </div>
                            </div>
                        </div>
                        <div style={styles.headerActions}>
                            {isInitialized && (
                                <>
                                    <button onClick={handleRefresh} disabled={isLoading} style={styles.headerBtn} title="Refresh data">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="23 4 23 10 17 10" />
                                            <polyline points="1 20 1 14 7 14" />
                                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                        </svg>
                                    </button>
                                    <button onClick={handleClear} disabled={isLoading} style={styles.headerBtn} title="Clear conversation">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6l-2 14H7L5 6" />
                                            <path d="M10 11v6" />
                                            <path d="M14 11v6" />
                                        </svg>
                                    </button>
                                </>
                            )}
                            <button onClick={minimizeAthena} style={styles.headerBtn} title="Minimize">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </button>
                            {isInitialized && (
                                <button onClick={handleEndSession} style={{ ...styles.headerBtn, color: "#ef4444" }} title="End session">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Data sources bar */}
                    {dataSources && (
                        // @ts-ignore
                        <div style={styles.dataBar}>
                            {[
                                ["FFM", dataSources.ffm],
                                ["FWB", dataSources.fwb],
                                ["Rack", dataSources.rack],
                                ["Payments", dataSources.payments],
                                ["Queue", dataSources.queue],
                                ["Imports", dataSources.imports],
                            ].map(([label, count]) => (
                                <span key={label} style={{ ...styles.dataPill, ...(count > 0 ? styles.dataPillActive : {}) }}>
                                    {label}: {count}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Messages area */}
                    <div style={styles.messagesArea}>
                        {!isInitialized ? (
                            /* Auto-initializing screen */
                            // @ts-ignore
                            <div style={styles.welcomeWrap}>
                                <div style={styles.welcomeIcon}>
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                    </svg>
                                </div>
                                {isLoading ? (
                                    <>
                                        <h3 style={styles.welcomeTitle}>Analyzing Shipment Data...</h3>
                                        <p style={styles.welcomeText}>
                                            Loading FFM, FWB, warehouse locations, payments, and queue data for MAWB {mawb.replace(/^(\d{3})(\d+)$/, "$1-$2")}
                                        </p>
                                        <span style={styles.spinnerLarge} />
                                    </>
                                ) : error ? (
                                    <>
                                        <h3 style={styles.welcomeTitle}>Unable to Load Data</h3>
                                        <div style={styles.errorBanner}>{error}</div>
                                        <button onClick={handleInitialize} style={{ ...styles.startBtn, marginTop: 12 }}>
                                            Retry
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <h3 style={styles.welcomeTitle}>Athena AI Assistant</h3>
                                        <p style={styles.welcomeText}>Preparing chat for MAWB {mawb.replace(/^(\d{3})(\d+)$/, "$1-$2")}...</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            /* Chat Messages */
                            <>
                                {messages.map((msg, i) => (
                                    <div key={i} style={msg.role === "user" ? styles.msgRowUser : styles.msgRowAssistant}>
                                        {msg.role !== "user" && (
                                            <div style={msg.role === "system" ? styles.avatarSystem : styles.avatarAssistant}>
                                                <i className={'fa-duotone fa-solid fa-microchip-ai'} style={{ fontSize: '20px' }} />
                                            </div>
                                        )}
                                        <div
                                            style={
                                                msg.role === "user"
                                                    ? styles.bubbleUser
                                                    : msg.role === "system"
                                                        ? styles.bubbleSystem
                                                        : styles.bubbleAssistant
                                            }
                                        >
                                            {msg.role === "assistant" ? (
                                                <div style={styles.markdownContent}>{formatMarkdown(msg.content)}</div>
                                            ) : (
                                                <div style={{ whiteSpace: "pre-wrap" }}>{msg.content}</div>
                                            )}
                                            <div style={styles.msgMeta}>
                                                <span>{formatTime(msg.timestamp)}</span>
                                                {msg.tokens && (
                                                    <span style={styles.tokenBadge}>{msg.tokens.total} tokens</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Loading indicator */}
                                {isLoading && (
                                    <div style={styles.msgRowAssistant}>
                                        <div style={styles.avatarAssistant}>AI</div>
                                        <div style={styles.bubbleAssistant}>
                                            <div style={styles.typingDots}>
                                                <span style={{ ...styles.dot, animationDelay: "0s" }} />
                                                <span style={{ ...styles.dot, animationDelay: "0.2s" }} />
                                                <span style={{ ...styles.dot, animationDelay: "0.4s" }} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Token warning */}
                                {tokenWarning && (
                                    <div style={styles.warningBanner}>
                                        ⚠ {tokenWarning}
                                        <button onClick={handleClear} style={styles.warningBtn}>Clear History</button>
                                    </div>
                                )}

                                {/* Error */}
                                {error && <div style={styles.errorBanner}>{error}</div>}

                                {/* Suggestion chips — show after first AI message if no user messages yet */}
                                {messages.length === 1 && messages[0].role === "assistant" && !isLoading && (
                                    <div style={styles.suggestionsWrap}>
                                        <div style={styles.suggestionsLabel}>Ask a question:</div>
                                        {/* @ts-ignore */}
                                        <div style={styles.suggestionsGrid}>
                                            {suggestions.map((s) => (
                                                <button
                                                    key={s}
                                                    onClick={() => {
                                                        setInputValue(s);
                                                        setTimeout(() => inputRef.current?.focus(), 50);
                                                    }}
                                                    style={styles.chip}
                                                >
                                                    {s}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>

                    {/* Input bar — only show when initialized */}
                    {isInitialized && (
                        <div style={styles.inputBar}>
                            <textarea
                                ref={inputRef}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask about this shipment..."
                                rows={1}
                                // @ts-ignore
                                style={styles.textInput}
                                disabled={isLoading}
                            />
                            <button
                                onClick={handleSendMessage}
                                disabled={!inputValue.trim() || isLoading}
                                style={{
                                    ...styles.sendBtn,
                                    ...(inputValue.trim() && !isLoading ? styles.sendBtnActive : {}),
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="22" y1="2" x2="11" y2="13" />
                                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Keyframe animations */}
            <style>{`
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </>
    );
}

// ============================================================
// Simple markdown-like formatting for AI responses
// ============================================================
function formatMarkdown(text) {
    if (!text) return null;

    return text.split("\n").map((line, i) => {
        // Headers
        if (line.startsWith("### ")) return <h4 key={i} style={{ margin: "10px 0 4px", fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{line.slice(4)}</h4>;
        if (line.startsWith("## ")) return <h3 key={i} style={{ margin: "12px 0 4px", fontSize: 14, fontWeight: 700, color: "#1e293b" }}>{line.slice(3)}</h3>;

        // Bullet points
        if (line.match(/^[-•]\s/)) {
            return (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 2, lineHeight: 1.5 }}>
                    <span style={{ color: "#16a34a", flexShrink: 0, marginTop: 1 }}>•</span>
                    <span>{inlineFormat(line.slice(2))}</span>
                </div>
            );
        }

        // Numbered items
        const numMatch = line.match(/^(\d+)\.\s/);
        if (numMatch) {
            return (
                <div key={i} style={{ display: "flex", gap: 6, marginBottom: 2, lineHeight: 1.5 }}>
                    <span style={{ color: "#16a34a", fontWeight: 600, flexShrink: 0, minWidth: 16 }}>{numMatch[1]}.</span>
                    <span>{inlineFormat(line.slice(numMatch[0].length))}</span>
                </div>
            );
        }

        // Warning/flag lines
        if (line.includes("⚠")) {
            return <div key={i} style={{ background: "#fef3c7", borderLeft: "3px solid #f59e0b", padding: "4px 8px", margin: "4px 0", borderRadius: 4, fontSize: 12.5 }}>{inlineFormat(line)}</div>;
        }

        // Check mark lines
        if (line.includes("✓")) {
            return <div key={i} style={{ background: "#f0fdf4", borderLeft: "3px solid #16a34a", padding: "4px 8px", margin: "4px 0", borderRadius: 4, fontSize: 12.5 }}>{inlineFormat(line)}</div>;
        }

        // Empty line
        if (!line.trim()) return <div key={i} style={{ height: 8 }} />;

        // Regular paragraph
        return <div key={i} style={{ marginBottom: 3, lineHeight: 1.55 }}>{inlineFormat(line)}</div>;
    });
}

function inlineFormat(text) {
    // Bold: **text**
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
            return <strong key={i} style={{ fontWeight: 600, color: "#0f172a" }}>{part.slice(2, -2)}</strong>;
        }
        return part;
    });
}

// ============================================================
// Styles — green/white operational theme matching Master View
// ============================================================
const G = {
    green: "#16a34a",
    greenDark: "#15803d",
    greenLight: "#f0fdf4",
    greenMuted: "#dcfce7",
    bg: "#f8fafc",
    white: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b",
    border: "#e2e8f0",
    borderLight: "#f1f5f9",
    shadow: "0 4px 24px rgba(0,0,0,0.12)",
    radius: 12,
};

const styles = {
    // -- FAB --
    fab: {
        position: "fixed",
        bottom: 24,
        right: 24,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "12px 20px",
        background: G.green,
        color: G.white,
        border: "none",
        borderRadius: 50,
        cursor: "pointer",
        boxShadow: "0 4px 16px rgba(22,163,74,0.35)",
        fontSize: 14,
        fontWeight: 600,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        zIndex: 9999,
        transition: "transform 0.15s, box-shadow 0.15s",
    },
    fabLabel: { letterSpacing: "0.01em" },

    // -- Panel --
    panel: {
        position: "fixed",
        bottom: 24,
        right: 24,
        width: 420,
        maxWidth: "calc(100vw - 48px)",
        height: 620,
        maxHeight: "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        background: G.white,
        borderRadius: G.radius,
        boxShadow: G.shadow,
        border: `1px solid ${G.border}`,
        overflow: "hidden",
        zIndex: 9999,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        animation: "slideUp 0.2s ease-out",
    },

    // -- Header --
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 16px",
        background: G.green,
        color: G.white,
        flexShrink: 0,
    },
    headerLeft: { display: "flex", alignItems: "center", gap: 10 },
    headerIcon: {
        width: 32,
        height: 32,
        borderRadius: 8,
        background: "rgba(255,255,255,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    headerTitle: { fontSize: 14, fontWeight: 700, letterSpacing: "0.01em" },
    headerSub: { fontSize: 11, opacity: 0.85, marginTop: 1 },
    headerActions: { display: "flex", gap: 4 },
    headerBtn: {
        background: "rgba(255,255,255,0.15)",
        border: "none",
        borderRadius: 6,
        color: G.white,
        width: 30,
        height: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "background 0.15s",
    },

    // -- Data sources bar --
    dataBar: {
        display: "flex",
        gap: 4,
        padding: "6px 12px",
        background: G.borderLight,
        borderBottom: `1px solid ${G.border}`,
        flexWrap: "wrap",
        flexShrink: 0,
    },
    dataPill: {
        fontSize: 10,
        fontWeight: 600,
        padding: "2px 7px",
        borderRadius: 10,
        background: G.white,
        color: G.textMuted,
        border: `1px solid ${G.border}`,
    },
    dataPillActive: {
        background: G.greenMuted,
        color: G.greenDark,
        borderColor: "#bbf7d0",
    },

    // -- Messages area --
    messagesArea: {
        flex: 1,
        overflow: "auto",
        padding: "12px 14px",
        background: G.bg,
    },

    // -- Welcome / MAWB entry --
    welcomeWrap: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        padding: "20px 16px",
        textAlign: "center",
    },
    welcomeIcon: {
        width: 64,
        height: 64,
        borderRadius: 16,
        background: G.greenLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
    },
    welcomeTitle: { fontSize: 17, fontWeight: 700, color: G.text, margin: "0 0 8px" },
    welcomeText: { fontSize: 13, color: G.textMuted, lineHeight: 1.5, margin: "0 0 20px", maxWidth: 320 },
    mawbInputWrap: { display: "flex", gap: 8, width: "100%", maxWidth: 300 },
    mawbInput: {
        flex: 1,
        padding: "10px 14px",
        border: `1.5px solid ${G.border}`,
        borderRadius: 8,
        fontSize: 14,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        outline: "none",
        transition: "border-color 0.15s",
        letterSpacing: "0.03em",
    },
    startBtn: {
        padding: "10px 18px",
        background: G.green,
        color: G.white,
        border: "none",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        transition: "background 0.15s",
    },

    // -- Message rows --
    msgRowUser: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: 10,
        animation: "slideUp 0.15s ease-out",
    },
    msgRowAssistant: {
        display: "flex",
        gap: 8,
        marginBottom: 10,
        alignItems: "flex-start",
        animation: "slideUp 0.15s ease-out",
    },

    // -- Avatars --
    avatarAssistant: {
        width: 28,
        height: 28,
        borderRadius: 8,
        background: G.green,
        color: G.white,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 800,
        flexShrink: 0,
        marginTop: 2,
    },
    avatarSystem: {
        width: 28,
        height: 28,
        borderRadius: 8,
        background: "#e0e7ff",
        color: "#4f46e5",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        flexShrink: 0,
        marginTop: 2,
    },

    // -- Bubbles --
    bubbleUser: {
        maxWidth: "80%",
        padding: "10px 14px",
        background: G.green,
        color: G.white,
        borderRadius: "12px 12px 4px 12px",
        fontSize: 13,
        lineHeight: 1.5,
    },
    bubbleAssistant: {
        maxWidth: "85%",
        padding: "10px 14px",
        background: G.white,
        color: G.text,
        borderRadius: "12px 12px 12px 4px",
        fontSize: 13,
        lineHeight: 1.5,
        border: `1px solid ${G.border}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    bubbleSystem: {
        maxWidth: "85%",
        padding: "8px 12px",
        background: "#eef2ff",
        color: "#4338ca",
        borderRadius: 8,
        fontSize: 12,
        fontStyle: "italic",
    },
    markdownContent: { fontSize: 13 },
    msgMeta: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: 6,
        fontSize: 10,
        opacity: 0.5,
    },
    tokenBadge: {
        background: "rgba(0,0,0,0.06)",
        padding: "1px 6px",
        borderRadius: 4,
        fontSize: 9,
    },

    // -- Typing indicator --
    typingDots: { display: "flex", gap: 4, padding: "4px 0" },
    dot: {
        width: 7,
        height: 7,
        borderRadius: "50%",
        background: G.green,
        animation: "dotPulse 1.2s infinite ease-in-out",
        display: "inline-block",
    },

    // -- Suggestions --
    suggestionsWrap: { marginTop: 8, padding: "0 4px" },
    suggestionsLabel: { fontSize: 11, color: G.textMuted, marginBottom: 6, fontWeight: 600 },
    suggestionsGrid: { display: "flex", flexWrap: "wrap", gap: 6 },
    chip: {
        padding: "6px 11px",
        background: G.white,
        border: `1px solid ${G.border}`,
        borderRadius: 20,
        fontSize: 11.5,
        color: G.text,
        cursor: "pointer",
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        transition: "all 0.15s",
        lineHeight: 1.3,
    },

    // -- Input bar --
    inputBar: {
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        padding: "10px 14px",
        borderTop: `1px solid ${G.border}`,
        background: G.white,
        flexShrink: 0,
    },
    textInput: {
        flex: 1,
        padding: "10px 12px",
        border: `1.5px solid ${G.border}`,
        borderRadius: 10,
        fontSize: 13,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        resize: "none",
        outline: "none",
        maxHeight: 80,
        lineHeight: 1.4,
        transition: "border-color 0.15s",
    },
    sendBtn: {
        width: 38,
        height: 38,
        borderRadius: 10,
        border: "none",
        background: G.border,
        color: G.textMuted,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flexShrink: 0,
        transition: "all 0.15s",
    },
    sendBtnActive: {
        background: G.green,
        color: G.white,
        boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
    },

    // -- Banners --
    errorBanner: {
        margin: "8px 0",
        padding: "8px 12px",
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: 8,
        color: "#dc2626",
        fontSize: 12,
    },
    warningBanner: {
        margin: "8px 0",
        padding: "8px 12px",
        background: "#fffbeb",
        border: "1px solid #fde68a",
        borderRadius: 8,
        color: "#92400e",
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
    },
    warningBtn: {
        padding: "4px 10px",
        background: "#fbbf24",
        border: "none",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        color: "#78350f",
        flexShrink: 0,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
    },

    // -- Spinner --
    spinner: {
        width: 16,
        height: 16,
        border: "2px solid rgba(255,255,255,0.3)",
        borderTopColor: G.white,
        borderRadius: "50%",
        display: "inline-block",
        animation: "spin 0.6s linear infinite",
    },
    spinnerLarge: {
        width: 28,
        height: 28,
        border: "3px solid #dcfce7",
        borderTopColor: G.green,
        borderRadius: "50%",
        display: "inline-block",
        animation: "spin 0.7s linear infinite",
        marginTop: 12,
    },
};